import { Component, Injector, Input, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MasterService } from '../../../../../../services/master.service'
import { MessageService } from '../../../../../../services/message.service'
import * as languageLibrary from '../../../../../../services/language'
import { PAYMENT_CYCLES } from '../../../../../../constants/sale'
import { DynamicService } from 'src/app/services/dynamic.service';
import { token } from '../../roles/roles.component';
import { Subscription } from "rxjs";

@Component({
    selector: '[role-floorplan]',
    templateUrl: './role-floorplan.component.html',
    styleUrls: ['./role-floorplan.component.scss'],
    standalone: false
})
export class ContactFloorPlanComponent implements OnInit {

  protected contactId: number = 0
  
  protected contactRoleId: number = 0
  
  private id: number = 0
  
  protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'
  
  protected words = languageLibrary.language
  
  protected loading: boolean = true
  
  protected isOpen: boolean = true
  
  protected paymentFrequency: string[] = PAYMENT_CYCLES
  
  protected dataIn: Object;

  // A handle to navbar event messages so we can unsubscribe later.
  private navbarWatcher: Subscription | null = null
  
  protected floorPlanInformation = new FormGroup({
    id: new FormControl<number | null>(null),
    contactId: new FormControl<number | null>(null, [Validators.required]),
    accountNumber: new FormControl<string | null>(null),
    apr: new FormControl<string | null>(null, [Validators.pattern('[0-9]{1,2}(\.[0-9]{1,4})?'), Validators.min(0), Validators.max(99.9999)]),
    daysPerYear: new FormControl<string | null>(null, [Validators.required, Validators.pattern('[0-9]{1,3}'), Validators.max(366)]),
    daysAllowed: new FormControl<string | null>(null, [Validators.required, Validators.pattern('[0-9]{1,3}')]),
    reductionAmount: new FormControl<string | null>(null),
    reductionFrequency: new FormControl<string>('Monthly'),
    feeAmount: new FormControl<string | null>(null),
    initialFeeAmount: new FormControl<string | null>(null),
  })
  protected calledFrom: string;

  get accountNumber() { return this.floorPlanInformation.get('accountNumber') }
  get apr() { return this.floorPlanInformation.get('apr') }
  get daysPerYear() { return this.floorPlanInformation.get('daysPerYear') }
  get daysAllowed() { return this.floorPlanInformation.get('daysAllowed') }
  get reductionAmount() { return this.floorPlanInformation.get('reductionAmount') }
  get reductionFrequency() { return this.floorPlanInformation.get('reductionFrequency') }
  get feeAmount() { return this.floorPlanInformation.get('feeAmount') }
  get initialFeeAmount() { return this.floorPlanInformation.get('initialFeeAmount') }


  /*
  * Life Cycles
  */
  constructor(private master: MasterService, private ms: MessageService, private inject: Injector, private serv: DynamicService) {
    this.navbarWatcher = this.ms.channelComponents$.subscribe(res => {
      if (res.message == "changeLanguage") {
        this.language = res.value
      }
    })
  }

  deactivateRole() {
    this.serv.outputFromDynamicComponent({ action: "deactivate", contactRoleId: this.contactRoleId });
  }

  ngOnInit() {
    this.dataIn = this.inject.get(token);
    this.contactId = this.dataIn['contactId']
    this.contactRoleId = this.dataIn['contactRoleId']
    this.calledFrom = this.dataIn['calledFrom']

    if (!this.contactId) {
      return
    }

    this.floorPlanInformation.get("contactId").setValue(this.contactId)

    this.getFloorPlan()
  }

  ngOnDestroy() {
    if (!this.navbarWatcher) {
      return
    }

    this.navbarWatcher.unsubscribe()
  }

  /*
  * functions
  */
  protected setInformation(contactFloorPlan) {
    this.floorPlanInformation.setValue({
      id: contactFloorPlan['id'],
      contactId: contactFloorPlan['contactId'],
      accountNumber: contactFloorPlan['accountNumber'],
      apr: contactFloorPlan['apr'],
      daysPerYear: contactFloorPlan['daysPerYear'],
      daysAllowed: contactFloorPlan['daysAllowed'],
      reductionAmount: contactFloorPlan['reductionAmount'],
      reductionFrequency: contactFloorPlan['reductionFrequency'],
      feeAmount: contactFloorPlan['feeAmount'],
      initialFeeAmount: contactFloorPlan['initialFeeAmount'],
    })

    this.id = contactFloorPlan['id']
    this.contactId = contactFloorPlan['contactId']
  }

  protected save() {
    const identity = this.floorPlanInformation.get('contactId').value
    if (!identity || isNaN(identity)) {
      return
    }

    const id = this.floorPlanInformation.get('id').value
    if (!id || isNaN(id) || (id <= 0)) {
      this.createFloorPlanSetup()
      return
    }

    this.modifyFloorPlanSetup(id)
  }

  protected createFloorPlanSetup = (): void => {
    if (this.floorPlanInformation.invalid) {
      return
    }

    this.floorPlanInformation.get('contactId').setValue(this.contactId)
    this.master.post(`contacts/${this.contactId}/floorplan`, { information: this.floorPlanInformation.value, }, res => {
      if (!res) {
        this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
        return
      }

      if (res.status !== 200) {
        this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
        return
      }

      if (!res.data.contactFloorPlan){
        return
      }
      
      this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['saved'] })

      this.floorPlanInformation.get('id').patchValue(res.data.contactFloorPlan['id'])

      if (this.calledFrom == "contactAdvancedSearch")
        this.serv.outputFromDynamicComponent({ action: "setupSaved", setupSaved: true, type:"floorPlanVendor" });
    })
  }

  protected modifyFloorPlanSetup = (id): void => {
    if (this.floorPlanInformation.invalid) {
      return
    }

    this.floorPlanInformation.get('contactId').setValue(this.contactId)
    this.master.put(`contacts/${this.contactId}/floorplan/${id}`, { information: this.floorPlanInformation.value }, res => {
      if (!res) {
        this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
        return
      }

      if (res.status !== 200) {
        this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
        return
      }

      this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['saved'] })

      if (this.calledFrom == "contactAdvancedSearch")
        this.serv.outputFromDynamicComponent({ action: "setupSaved", setupSaved: true, type:"floorPlanVendor" });

    })
  }

  protected clear = (): void => {
    this.id = this.floorPlanInformation.get('id').value ?? 0
    this.floorPlanInformation.reset()
    if ((this.contactId <= 0) || (this.id <= 0) || (isNaN(this.id))) {
      return
    }

    this.master.discard(`contacts/${this.contactId}/floorplan/${this.id}`, res => {
      if (!res) {
        this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
        return
      }

      if (res.status !== 200) {
        this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
        return
      }

    })

  }

  private getFloorPlan() {
    this.loading = true
    this.master.get(`contacts/${this.contactId}/floorplan`, res => {
      this.loading = false
      if (!res) {
        this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
        return
      }

      if (res.status !== 200) {
        this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
        return
      }

      if (!res.data.contactFloorPlan) {
        if (this.calledFrom == "contactAdvancedSearch")
        this.serv.outputFromDynamicComponent({ action: "setupNeeded", setupNeeded: true, type:"floorPlanVendor" });
        return
      }

      this.setInformation(res.data.contactFloorPlan)

      if (this.calledFrom == "contactAdvancedSearch") {
        this.serv.outputFromDynamicComponent({ action: "setupNeeded", setupNeeded: false, type:"floorPlanVendor" });
        return
      }
    })
  }
}
